/**
 * 전화기 sub-tab(PbxDevice*) 컴포넌트끼리만 쓰는 작은 헬퍼.
 * 공용 판정(pbxExtensions.js)에 없는 표시 규칙만 둔다 — 다른 sub-tab 과 공유할 게 생기면 그쪽으로 올린다.
 * 계약: ~/md/front/studio-내선·전화기·사이트-탭-요청.md "화면 명세 › 전화기"
 */
import { Modal } from 'ant-design-vue';

import { defaultSipPort } from '@/views/Callbot/service/pbxExtensions';

/**
 * 확인창 — 취소하면 false. 버튼 문구는 spec 의 "확인 버튼" 열을 따른다
 * @param {{ title: string; content: string; okText: string; danger?: boolean }} opts
 * @returns {Promise<boolean>}
 */
export function confirmPbx(opts) {
  return new Promise((resolve) => {
    Modal.confirm({
      title: opts.title,
      content: opts.content,
      okText: opts.okText,
      okType: opts.danger ? 'danger' : 'primary',
      cancelText: '취소',
      onOk: () => resolve(true),
      onCancel: () => resolve(false),
    });
  });
}

/**
 * 확인 문구의 `{label}` — 라벨이 없으면 MAC
 * @param {{ label?: string | null; mac: string } | null | undefined} d
 * @returns {string}
 */
export function deviceName(d) {
  if (!d) return '';
  return d.label?.trim() || d.mac;
}

/**
 * 목록 항목에 UA 가 없을 수 있다 — 마지막 설정 수신 때의 UA 로 대신한다
 * @param {Object} d PbxDeviceItem
 * @returns {string}
 */
export function deviceUserAgent(d) {
  return d.user_agent || d.last_provision?.user_agent || '—';
}

/**
 * @typedef {Object} EffectiveTransport
 * @property {'tls' | 'udp' | 'tcp'} transport
 * @property {number} port
 * @property {boolean} srtp
 */

/**
 * 전화기에 실제로 적용되는 전송 — 전화기 값이 null 이면 설치 장소 기본값, 설치 장소도 모르면 계약 기본(TLS 5061).
 * 전송 전환 확인 문구의 "from" 과 모달 초기값에 쓴다.
 * @param {Object} d PbxDeviceItem
 * @param {Object | null | undefined} site PbxSiteItem
 * @returns {EffectiveTransport}
 */
export function effectiveTransport(d, site) {
  const transport = d.sip_transport ?? site?.sip_transport_default ?? 'tls';
  const port =
    d.sip_port ??
    (d.sip_transport ? defaultSipPort(transport) : site?.sip_port_default ?? defaultSipPort(transport));
  const srtp = d.srtp_require ?? site?.srtp_require ?? false;
  return { transport, port, srtp };
}

/**
 * `TLS 5061` / `UDP 5060`
 * @param {'tls' | 'udp' | 'tcp'} transport
 * @param {number | null=} port
 * @returns {string}
 */
export function transportText(transport, port) {
  return `${transport.toUpperCase()} ${port ?? defaultSipPort(transport)}`;
}

/**
 * 1회 표시 값 — 결과 모달(PbxDeviceRevealModal)이 닫히면 버린다
 * @typedef {{ kind: 'adminPassword'; value: string } | { kind: 'recoveryCode'; code: string; expires_at: number; recovery_url: string }} PbxDeviceReveal
 */

/**
 * 입력칸 Enter 제출 — 한글 IME 조합 중 Enter 는 조합 확정이라 제출하지 않는다
 * @param {KeyboardEvent} e
 * @returns {boolean}
 */
export function isImeEnter(e) {
  return e.isComposing || e.keyCode === 229;
}
